import { useEffect, useState } from "react";
import { api } from "./lib/api";

// ── TEXTING REVIEWS (0404) ──────────────────────────────────────────────────────────────────────
//
// Caleb, 2026-10-29: texting setup files itself, checked by Orator's AI reviewer -- "the verdict log ...
// phenomenal", kept here in the Portal, not the campaign dashboard. Every review a campaign's filing ran
// (and every staff test): what it said, which model, what it cost. "Test the reviewer" sends a tiny
// made-up application through the real reviewer -- nothing is filed -- so it can be checked after a
// deploy without waiting for a campaign.

type Review = { at: string; campaign: string | null; kind: "filing" | "test"; model: string | null; ready: boolean | null;
  fixes: string[]; input_tokens: number | null; output_tokens: number | null; cost_cents: number | null; seconds: number | null;
  error: string | null };
type Data = { reviews: Review[]; month_cost_cents: number; month_count: number; model: string; pinned: boolean;
  model_chosen_at: string | null; configured: boolean; per_day_cap: number };
type Test = { ready: boolean; fixes: string[]; model: string; cost_cents: number; seconds: number };

const cents = (c: number | null | undefined) => c == null ? "—" : c < 100 ? `${c.toFixed(1)}¢` : `$${(c / 100).toFixed(2)}`;
const when = (s: string) => new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export function TextingReviews() {
  const [d, setD] = useState<Data | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>("load");
  const [test, setTest] = useState<Test | null>(null);
  const [testErr, setTestErr] = useState<string | null>(null);
  const load = () => {
    setBusy("load"); setErr(null);
    api<Data>("/staff/texting/reviews").then(setD).catch((e) => setErr(String(e?.message ?? e))).finally(() => setBusy(null));
  };
  useEffect(load, []);
  const runTest = () => {
    setBusy("test"); setTest(null); setTestErr(null);
    api<Test>("/staff/texting/review-test", { method: "POST" })
      .then((t) => { setTest(t); load(); })
      .catch((e) => { setTestErr(String(e?.message ?? e)); load(); })
      .finally(() => setBusy(null));
  };
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-bold">Texting reviews</h2>
        <button onClick={load} disabled={!!busy} className="text-sm rounded-lg border border-border px-3 py-1 disabled:opacity-50">
          {busy === "load" ? "Loading…" : "Refresh"}
        </button>
      </div>
      {err && <div className="err">Couldn't load. {err}</div>}
      {d && (
        <div className="rounded-xl border border-border px-4 py-3 text-sm grid gap-1">
          <div><b>Reviewer:</b> {d.configured ? <>{d.model}{d.pinned ? " (pinned in Render)" : " (newest Sonnet, checked daily)"}</> : <span className="text-red-700 font-bold">no API key on this service</span>}</div>
          <div><b>This month:</b> {d.month_count} review{d.month_count === 1 ? "" : "s"}, {cents(d.month_cost_cents)}. A campaign can run {d.per_day_cap} a day.</div>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <button onClick={runTest} disabled={!!busy || !d.configured} className="text-sm rounded-lg bg-black text-white px-3 py-1.5 disabled:opacity-50">
              {busy === "test" ? "Testing… (up to a minute)" : "Test the reviewer"}
            </button>
            <span className="text-muted">Sends a made-up application through the real reviewer. Nothing is filed.</span>
          </div>
          {test && <div className="text-green-800">Working: {test.model} answered {test.ready ? "ready" : "not ready"} in {test.seconds}s, {cents(test.cost_cents)}.</div>}
          {testErr && <div className="err">Not working. {testErr}</div>}
        </div>
      )}
      {d && (
        <div className="rounded-xl border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted border-b border-border">
              <th className="px-3 py-2">When</th><th className="px-3 py-2">Campaign</th><th className="px-3 py-2">Verdict</th>
              <th className="px-3 py-2">Model</th><th className="px-3 py-2 text-right">Cost</th>
            </tr></thead>
            <tbody>
              {d.reviews.length === 0 && <tr><td colSpan={5} className="px-3 py-3 text-muted">No reviews yet.</td></tr>}
              {d.reviews.map((r, i) => (
                <tr key={i} className="border-b border-border last:border-0 align-top">
                  <td className="px-3 py-1.5 whitespace-nowrap">{when(r.at)}</td>
                  <td className="px-3 py-1.5">{r.kind === "test" ? <span className="text-muted">Staff test</span> : (r.campaign ?? "—")}</td>
                  <td className="px-3 py-1.5">
                    {r.error ? <span className="text-red-700">Couldn't run: {r.error}</span>
                      : r.ready ? <span className="text-green-800 font-semibold">Ready, filed</span>
                      : <><span className="text-amber-700 font-semibold">Fixes asked for</span>
                          {r.fixes.length > 0 && <ol className="list-decimal pl-5 text-muted">{r.fixes.map((f, j) => <li key={j}>{f}</li>)}</ol>}</>}
                  </td>
                  <td className="px-3 py-1.5 text-muted whitespace-nowrap">{r.model ?? "—"}{r.seconds != null ? ` · ${r.seconds}s` : ""}</td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">{cents(r.cost_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
